import { TOKENS_PER_PLAYER } from "./types.js";
const counters = () => ({ 0: 0, 1: 0, 2: 0, 3: 0 });
export function createMatchStatistics() {
    return { rolls: counters(), moves: counters(), captures: counters(), homes: counters() };
}
/**
 * Creates a complete deterministic board state. Empty seats are accepted in a
 * setup payload but deliberately omitted from the active match and token list.
 */
export function createMatch(id, mode, seats, seedCommit, now, rules = {}) {
    const activeSeats = seats.filter((seat) => seat.status !== "empty");
    if (activeSeats.length < 1 || activeSeats.length > 4)
        throw new Error("A match requires one to four active seats");
    const uniqueSeats = new Set(activeSeats.map((seat) => seat.seat));
    if (uniqueSeats.size !== activeSeats.length)
        throw new Error("Each active seat must use a unique colour");
    if (activeSeats.some((seat) => !Number.isInteger(seat.seat) || seat.seat < 0 || seat.seat > 3))
        throw new Error("Seat colours must be between 0 and 3");
    const fullRules = {
        autoMove: false,
        suddenDeath: false,
        turnSeconds: mode === "ranked" ? 20 : mode === "tournament" ? 60 : 30,
        teams: activeSeats.some((seat) => seat.team !== undefined && seat.team !== null),
        blocks: true,
        entryRoll: 6,
        safeCellsShareable: true,
        extraTurnOnDoubles: true,
        extraTurnOnCapture: true,
        extraTurnOnHome: true,
        extraTurnOnEntryRoll: false,
        ...rules,
    };
    if (!Number.isInteger(fullRules.entryRoll) || fullRules.entryRoll < 1 || fullRules.entryRoll > 6)
        throw new Error("entryRoll must be between 1 and 6");
    const players = activeSeats.map((seat) => ({
        seat: seat.seat,
        userId: seat.userId,
        displayName: seat.displayName,
        team: seat.team ?? null,
        bot: seat.bot ?? null,
        status: seat.status ?? (seat.bot ? "bot" : "human"),
        rating: seat.rating ?? null,
        consecutiveTimeouts: 0,
        consecutiveDoubles: 0,
    }));
    const playable = players.filter((player) => player.status === "human" || player.status === "bot");
    if (playable.length < 1)
        throw new Error("A match requires at least one playable seat");
    const turn = playable.map((player) => player.seat).sort((a, b) => a - b)[0];
    if (turn === undefined)
        throw new Error("A match requires an active player");
    return {
        id,
        mode,
        seedCommit,
        turn,
        die: null,
        sixStreak: 0,
        rollNonce: 0,
        tokens: players.flatMap((player) => Array.from({ length: TOKENS_PER_PLAYER }, (_, index) => ({ id: `${id}:${player.seat}:${index}`, color: player.seat, cell: -1 }))),
        players,
        finished: [],
        phase: "roll",
        deadlineTs: now + fullRules.turnSeconds * 1000,
        rules: fullRules,
        lastRoll: null,
        stats: createMatchStatistics(),
        dice: [],
        lastMoved: { 0: null, 1: null, 2: null, 3: null },
        turnRewardPending: null,
    };
}
